/**
 * The six signalling operations, and nothing about WebRTC.
 *
 * relay-server proxies Cloudflare's Realtime SFU behind two route prefixes —
 * `/api/relay/calls/:conversationId` and `/api/relay/meetings/:id` — and the
 * six operations underneath them are identical (`relay-server/worker/lib/callproxy.ts`
 * is literally one file for both). So `Room` picks the prefix and everything
 * else here is the same code, exactly as the desktop's `RelayApi::Room` does
 * it. The alternative — a call client and a meeting client — duplicates the
 * part that was hard to get right and leaves one of the copies unfixed.
 *
 * ── Publishing and subscribing are not symmetrical ──────────────────────────
 * `publish` is us describing what we will send: our offer, the SFU's answer.
 * `pull` reverses it — the SFU describes what it is about to send and we
 * answer, then hand that answer back through `renegotiate`. That asymmetry is
 * the SFU's, and it is the single easiest thing in this feature to get
 * backwards. It lives in `session.ts`; this file only carries the shapes.
 *
 * ── Two roster paths, because only one of them exists on both ───────────────
 * A conversation answers its roster from `GET /api/relay/calls/:c`, a meeting
 * from `GET /api/relay/meetings/:id/roster`. Both take `?deviceId=` and both
 * touch that seat on the way through, which is what makes the poll the
 * heartbeat rather than a second request beside one.
 */

import { api } from '../api';
import type { MeetingRole } from '../relay/types';
import type { Room } from './types';

/** One seat, as `callproxy.ts`'s `RosterSeat` hands it over. The last four are
 *  optional because a conversation has no column for them; a meeting seat
 *  carries all eight. */
export interface RosterSeat {
  userId: string;
  deviceId: string;
  sessionId: string;
  /** What this session has published. A joiner pulls these by name without a
   *  round trip to the SFU per participant. */
  trackNames: string[];
  joinedAt: number;
  displayName?: string;
  guest?: boolean;
  role?: MeetingRole;
  muted?: boolean;
}

/** `callId` and `since` are a conversation's: a meeting *is* the room and its
 *  start is its own row. */
export interface RoomRoster {
  roster: RosterSeat[];
  callId?: string;
  since?: number;
  /** A meeting's recording flag, which every roster read carries: it is what
   *  the consent banner is drawn from, and what starts and stops each
   *  participant's own uploader. */
  recording?: boolean;
}

export interface SessionAnswer extends RoomRoster {
  sessionId: string;
}

/**
 * What `DELETE …/session` answers.
 *
 * `last` is true for whoever emptied the roster, and it is the whole of who
 * writes the durable `callend` — the rule changed on 2026-09-17 when calls
 * became group calls and the caller stopped being the one end that could know.
 * Every field is optional because a meeting's room answers the same route and
 * a conversation's `since` and `callId` are cleared by the time it replies.
 */
export interface LeaveAnswer {
  ok?: boolean;
  roster?: RosterSeat[];
  last?: boolean;
  since?: number;
  callId?: string;
}

export interface SessionDescriptionInit {
  sdp: string;
  type: string;
}

export interface SfuTrackResult {
  trackName?: string;
  mid?: string;
  errorCode?: string;
  errorDescription?: string;
}

export interface TracksAnswer {
  requiresImmediateRenegotiation?: boolean;
  tracks?: SfuTrackResult[];
  sessionDescription?: SessionDescriptionInit;
}

export interface LocalTrackSpec {
  location: 'local';
  mid: string;
  trackName: string;
}

export interface RemoteTrackSpec {
  location: 'remote';
  sessionId: string;
  trackName: string;
}

/** What a published track may be called. The prefix is the contract: a
 *  meeting's policy is enforced on it server-side, before the SFU is called,
 *  so a tidier naming scheme silently disables screen sharing for everybody. */
export type TrackKind = 'mic' | 'cam' | 'screen';

export function trackName(kind: TrackKind, suffix: string): string {
  return `${kind}-${suffix}`;
}

export function trackKind(name: string): TrackKind | null {
  const dash = name.indexOf('-');
  const prefix = dash < 0 ? name : name.slice(0, dash);
  return prefix === 'mic' || prefix === 'cam' || prefix === 'screen' ? prefix : null;
}

/** The route prefix for this room. */
export function roomPath(room: Room): string {
  return room.kind === 'call'
    ? `/api/relay/calls/${encodeURIComponent(room.id)}`
    : `/api/relay/meetings/${encodeURIComponent(room.id)}`;
}

/** Where the roster lives, which is not the same path in both rooms. */
export function rosterPath(room: Room): string {
  return room.kind === 'call' ? roomPath(room) : `${roomPath(room)}/roster`;
}

export interface Signalling {
  readonly room: Room;
  join(deviceId: string, callId?: string): Promise<SessionAnswer>;
  /** Also the heartbeat: the read touches this device's seat. */
  roster(deviceId: string): Promise<RoomRoster>;
  publish(
    sessionId: string,
    offer: SessionDescriptionInit,
    tracks: LocalTrackSpec[]
  ): Promise<TracksAnswer>;
  pull(sessionId: string, tracks: RemoteTrackSpec[]): Promise<TracksAnswer>;
  renegotiate(sessionId: string, answer: SessionDescriptionInit): Promise<void>;
  closeTracks(
    sessionId: string,
    mids: string[],
    sessionDescription?: SessionDescriptionInit
  ): Promise<TracksAnswer>;
  leave(deviceId: string): Promise<LeaveAnswer>;
}

/** `api` with its generic kept, so a test can hand over a fake without every
 *  call site casting. */
export type Request = <T>(path: string, init?: RequestInit) => Promise<T>;

export function relaySignalling(room: Room, request: Request = api): Signalling {
  const base = roomPath(room);
  const post = <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) });

  return {
    room,

    join(deviceId, callId) {
      return post<SessionAnswer>(`${base}/session`, {
        deviceId,
        ...(callId ? { callId } : {})
      });
    },

    roster(deviceId) {
      return request<RoomRoster>(
        `${rosterPath(room)}?deviceId=${encodeURIComponent(deviceId)}`
      );
    },

    publish(sessionId, offer, tracks) {
      return post<TracksAnswer>(`${base}/tracks`, {
        sessionId,
        sessionDescription: offer,
        tracks
      });
    },

    // No `sessionDescription`: the SFU is being asked what it will send, and it
    // answers with an offer of its own. Sending one here is the asymmetry got
    // backwards.
    pull(sessionId, tracks) {
      return post<TracksAnswer>(`${base}/tracks`, { sessionId, tracks });
    },

    async renegotiate(sessionId, answer) {
      await request<unknown>(`${base}/renegotiate`, {
        method: 'PUT',
        body: JSON.stringify({ sessionId, sessionDescription: answer })
      });
    },

    // Keyed on `mid`, never `trackName`: the SFU closes the media line, not the
    // name a client gave it.
    closeTracks(sessionId, mids, sessionDescription) {
      return request<TracksAnswer>(`${base}/tracks/close`, {
        method: 'PUT',
        body: JSON.stringify({
          sessionId,
          tracks: mids.map((mid) => ({ mid })),
          ...(sessionDescription ? { sessionDescription } : {}),
          force: false
        })
      });
    },

    // The answer is read rather than discarded: `last` is the only thing that
    // says whether this browser is the one that owes the conversation a
    // `callend` row.
    leave(deviceId) {
      return request<LeaveAnswer>(
        `${base}/session?deviceId=${encodeURIComponent(deviceId)}`,
        { method: 'DELETE' }
      );
    }
  };
}

const DEVICE_KEY = 'relay.deviceId';

/**
 * This browser's device id, minted once and kept.
 *
 * A roster is keyed by (account, device), so this is what makes two tabs of one
 * account two participants rather than a collision — and what makes a reload
 * take its own seat back instead of leaving a ghost in the roster for 45
 * seconds. The desktop takes it from the sealed store; a browser has nothing of
 * the sort, so `localStorage` is the whole of it.
 *
 * `isValidId` on the server is `[A-Za-z0-9_-]{1,64}`, which is what the shape
 * below stays inside. A browser with storage blocked gets a per-session id: a
 * seat that does not survive a reload beats no call at all.
 */
let memoryDeviceId = '';

export function browserDeviceId(): string {
  const mint = (): string => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    let out = '';
    for (const b of bytes) out += b.toString(36).padStart(2, '0');
    return `web_${out}`;
  };

  try {
    const saved = localStorage.getItem(DEVICE_KEY);
    if (saved) return saved;
    const fresh = mint();
    localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    if (!memoryDeviceId) memoryDeviceId = mint();
    return memoryDeviceId;
  }
}
